import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  isApiError,
  type PromoteProjectRequest,
  useApprovePromotionRequest,
  useRejectPromotionRequest,
} from "@/lib/api";
import { readApiErrorMessage } from "@/lib/api-errors";
import { logger } from "@/lib/logger";

import type { PendingDecision } from "./types";

type SentryLevel = NonNullable<PromoteProjectRequest["sentry_level"]>;

// The same gradations the project's Promote button offers (NOR-409).
const SENTRY_LEVEL_LABELS: Record<SentryLevel, string> = {
  off: "Off",
  errors: "Errors only",
  errors_performance: "Errors + performance",
  full: "Full (+ session replay)",
};

interface DecideDialogProps {
  decision: PendingDecision | null;
  onOpenChange: (open: boolean) => void;
  /** Close *decision*'s dialog, if it is still the one open. */
  onDone: (decision: PendingDecision) => void;
}

export function DecideDialog({ decision, onOpenChange, onDone }: DecideDialogProps) {
  return (
    <Dialog open={decision !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {decision && (
          // Remount per decision so a note never carries into another request.
          <DecideForm
            key={`${decision.request.id}-${decision.action}`}
            decision={decision}
            onClose={() => onDone(decision)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function DecideForm({ decision, onClose }: { decision: PendingDecision; onClose: () => void }) {
  const { request, action } = decision;
  const approve = useApprovePromotionRequest();
  const reject = useRejectPromotionRequest();
  const [note, setNote] = useState("");
  const [sentryLevel, setSentryLevel] = useState<SentryLevel>("off");
  const [error, setError] = useState<string | null>(null);
  const pending = approve.isPending || reject.isPending;
  const isApprove = action === "approve";
  // The backend requires a reason to reject (the requester reads it).
  const canSubmit = !pending && (isApprove || note.trim().length > 0);
  // A promotion outlives a closed dialog; its outcome then goes to a toast.
  const formOpen = useRef(true);
  useEffect(() => {
    formOpen.current = true;
    return () => {
      formOpen.current = false;
    };
  }, []);

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    setError(null);
    try {
      if (isApprove) {
        await approve.mutateAsync({
          requestId: request.id,
          projectId: request.project_id,
          sentryLevel: request.ai_enabled ? sentryLevel : "off",
          note: note.trim() || undefined,
        });
        toast.success(`${request.project_name} promoted to ${request.target}.`);
      } else {
        await reject.mutateAsync({ requestId: request.id, note: note.trim() });
        toast.success("Request rejected.");
      }
      onClose();
    } catch (err) {
      // A proxy stopped waiting (nginx's 60 s read timeout), usually while the
      // promotion carries on -- unless it timed out before reaching the
      // backend. The queue polls and shows which.
      if (isApprove && isApiError(err) && err.status === 504) {
        toast.info(`${request.project_name} may still be promoting. The list shows the result.`);
        onClose();
        return;
      }
      logger.error(`[promotion-requests] ${action} failed:`, err);
      const message = readApiErrorMessage(
        err,
        `Failed to ${action} the request. Please try again.`,
      );
      if (formOpen.current) {
        setError(message);
      } else {
        toast.error(`${request.project_name}: ${message}`);
      }
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {isApprove ? `Promote to ${request.target}` : "Reject promotion request"}
        </DialogTitle>
        <DialogDescription>
          <strong>{request.project_name}</strong> to {request.target}, requested by{" "}
          {request.requester_email}.{" "}
          {isApprove
            ? "Approving runs the promotion now: it creates the branch, opens the pull request and sets up the environment."
            : "Rejecting is final; the requester can ask again later."}
        </DialogDescription>
      </DialogHeader>

      <DialogBody>
        <div className="space-y-4">
          <div className="bg-muted/40 rounded-md p-3 text-sm">
            <p className="text-muted-foreground text-xs tracking-wider uppercase">Reason</p>
            <p className="mt-1">{request.reason}</p>
          </div>

          {/* Sentry is linked to AI-enabled projects only; the backend refuses it for others. */}
          {isApprove && request.ai_enabled && (
            <div className="space-y-1.5">
              <Label htmlFor="promotion-sentry-level">Link Sentry</Label>
              <Select value={sentryLevel} onValueChange={(v) => setSentryLevel(v as SentryLevel)}>
                <SelectTrigger id="promotion-sentry-level" className="w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SENTRY_LEVEL_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="promotion-note">
              {isApprove ? "Note (optional)" : "Reason (required)"}
            </Label>
            <Textarea
              id="promotion-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={
                isApprove ? "Anything the requester should know" : "What would need to change?"
              }
              rows={3}
              maxLength={2000}
              disabled={pending}
            />
          </div>

          {approve.isPending && (
            <p className="text-muted-foreground text-sm">
              This usually takes under a minute. You can close this window: the promotion carries
              on, and the list shows when it&apos;s done.
            </p>
          )}

          {error && <p className="text-destructive text-sm">{error}</p>}
        </div>
      </DialogBody>

      <DialogFooter>
        {/* Closing does not stop a running promotion, so it is not "Cancel". */}
        <Button variant="outline" onClick={onClose} disabled={reject.isPending}>
          {approve.isPending ? "Close" : "Cancel"}
        </Button>
        <Button
          variant={isApprove ? "default" : "destructive"}
          onClick={() => void handleSubmit()}
          disabled={!canSubmit}
        >
          {pending
            ? isApprove
              ? "Promoting…"
              : "Rejecting…"
            : isApprove
              ? `Approve and promote`
              : "Reject"}
        </Button>
      </DialogFooter>
    </>
  );
}
